import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAtom } from 'jotai';
import { userAtom, tokenAtom } from '../atoms/authAtoms';
import { logout } from '../utils/auth';
import { ROUTES } from '../constants';
import { AnimatePresence, motion } from 'framer-motion';
import { isAuthenticated, getCurrentUser, logoutUser, isAdmin as checkIfUserIsAdmin, isConfirmatrice } from '../login/controller/login-controller';
import CartIcon from './CartIcon';
import { useBranding } from '../hooks/useBranding';
import { COLORS } from '../constants';
import { useLanguage, LANGUAGES } from '../contexts/LanguageContext';
import AnnouncementBar from './templates/shared/AnnouncementBar';
import { surfaceColors } from '../utils/contrast';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useAtom(userAtom);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);
  const { language, setLanguage, t } = useLanguage();
  const [recentSearches] = useState(['Sustainable products', 'Premium bags', 'Eco gifts']);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults] = useState([
    { id: 1, name: 'Eco-friendly Water Bottle', category: 'Accessories' },
    { id: 2, name: 'Bamboo Toothbrush Set', category: 'Health' },
    { id: 3, name: 'Organic Cotton T-shirt', category: 'Clothing' },
  ]);
  const searchInputRef = useRef(null);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const [activeMegaMenuSection, setActiveMegaMenuSection] = useState('new');
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useAtom(tokenAtom);
  const { primaryColor, secondaryColor, storeLogo, shopName, brandingLoaded } = useBranding();
  
  // Use dynamic colors with fallbacks
  const colors = {
    primary: primaryColor || '#ff4d00',
    secondary: secondaryColor || '#fc7303'
  };
  // The header uses the boutique's secondary colour; text and accents adapt to stay readable on it.
  const header = surfaceColors(colors.secondary, colors.primary);

  useEffect(() => {
    setIsLoggedIn(!!token);
  }, [token]);
  // Handle click outside user menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setIsLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Focus search input when search modal opens
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [isSearchOpen]);

  useEffect(() => {
    const checkAuthStatus = () => {
      const authenticated = isAuthenticated();
      setIsLoggedIn(authenticated);

      if (authenticated) {
        const userData = getCurrentUser();
        setUser(userData);
      }
    };

    checkAuthStatus();

    // Listen for storage events to update navbar when auth changes in another tab
    window.addEventListener('storage', checkAuthStatus);

    return () => {
      window.removeEventListener('storage', checkAuthStatus);
    };
  }, [setUser]);

  const handleLogout = () => {
    logoutUser();
    setIsLoggedIn(false);
    setUser(null);
    setIsUserMenuOpen(false);
    navigate(ROUTES.login);
  };

  const isAdminUser = isLoggedIn && checkIfUserIsAdmin();
  // A confirmatrice works on orders only: her "Administration" link opens the orders, and she has no settings.
  const adminHome = isConfirmatrice() ? '/admin/orders' : ROUTES.admin;
  const canSeeSettings = isLoggedIn && !isConfirmatrice();

  // Escape key handler for modals
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        if (isSearchOpen) setIsSearchOpen(false);
        if (isUserMenuOpen) setIsUserMenuOpen(false);
        if (isMegaMenuOpen) setIsMegaMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [isSearchOpen, isUserMenuOpen, isMegaMenuOpen]);

  // Animation variants
  const fadeInAnimation = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2 } }
  };

  const searchAnimation = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } }
  };

  // Add this function to get the user avatar URL
  const getUserAvatar = () => {
    if (user && user.image) {
      return user.image;
    }
    // Generate a default avatar with user's initials if available
    if (user && (user.firstName || user.lastName)) {
      const initials = `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}`;
      return `https://ui-avatars.com/api/?name=${initials}&background=4983a9&color=fff&size=128`;
    }
    // Fallback to default image
    return "https://randomuser.me/api/portraits/men/40.jpg";
  };

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50">
      <AnnouncementBar bg={header.background} fg={header.text} border={header.border} />
      <nav
        className={`w-full transition-all duration-300 ${isScrolled
            ? 'shadow-lg py-2'
            : 'shadow-sm py-4'
          }`}
        style={{ backgroundColor: header.background }}
      >
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative flex items-center justify-between gap-4">
            {/* Centred logo, like the reference storefront — absolutely centred on mobile so it stays
                visually centred regardless of how wide the surrounding nav/action items are; reverts
                to a normal flex item (ordered via md:order-2) at the md breakpoint. */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 md:static md:left-auto md:top-auto md:translate-x-0 md:translate-y-0 flex shrink-0 justify-center md:order-2">
              <div className="flex-shrink-0 flex items-center">
                <Link to={ROUTES.home} className="group flex items-center space-x-2">
                  <div className="relative">
                    <div 
                      className="absolute -inset-0.5 rounded-full blur opacity-60 group-hover:opacity-80 transition duration-300"
                      style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.secondary})` }}
                    ></div>
                    <div className="relative w-10 h-10 flex items-center justify-center rounded-full bg-white shadow-sm overflow-hidden">
                      {brandingLoaded ? (
                        <img
                          src={storeLogo || "/logo.jpg"}
                          alt="Brand Logo"
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                          width="40"
                          height="40"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-200 animate-pulse" aria-hidden="true" />
                      )}
                    </div>
                  </div>
                  {/* The shop name only appears once the backend has answered —
                      no placeholder brand is shown in the meantime. */}
                  {brandingLoaded ? (
                    <span
                      className="font-bold text-xl tracking-tight"
                      style={{ color: header.accent }}
                    >{shopName}</span>
                  ) : (
                    <span className="h-6 w-32 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
                  )}
                </Link>
              </div>
            </div>

            <div className="flex items-center md:order-1 md:min-w-0 md:flex-1">
              <div className="hidden md:flex md:items-center md:space-x-8">
                {[
                  { to: ROUTES.home, label: t('nav.home'), visible: true },
                  { to: adminHome, label: t('nav.administration'), visible: isLoggedIn },
                  { to: '/admin/settings', label: t('nav.parametrage'), visible: canSeeSettings },
                  { to: '/documentation', label: t('nav.documentation'), visible: true },
                ].filter((link) => link.visible).map((link) => {
                  const isActive = location.pathname === link.to;
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={`relative overflow-hidden group py-5 ${isActive ? 'font-semibold' : ''}`}
                      style={{ color: isActive ? header.accent : header.muted }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = header.accent; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = isActive ? header.accent : header.muted; }}
                    >
                      <span className="relative z-10 text-sm tracking-wide uppercase transition-colors duration-200">{link.label}</span>
                      <span
                        className={`absolute bottom-0 left-0 w-full h-0.5 transform origin-left transition-transform duration-300 ease-out ${isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`}
                        style={{ backgroundColor: header.accent }}
                      ></span>
                    </Link>
                  );
                })}
              </div>
            </div>

            <div className="hidden md:order-3 md:flex md:min-w-0 md:flex-1 md:items-center md:justify-end md:space-x-5">
              {/* Search pill */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const q = (searchInputRef.current?.value || '').trim();
                  navigate(q ? `/catalog?search=${encodeURIComponent(q)}` : '/catalog');
                }}
                className="relative hidden lg:block lg:order-last"
                role="search"
              >
                <input
                  ref={searchInputRef}
                  type="search"
                  placeholder="Rechercher un produit…"
                  aria-label="Rechercher un produit"
                  className="w-64 rounded-full border border-[#ffac86] bg-white py-2 pl-4 pr-10 text-sm text-[#1c1917] outline-none transition-colors placeholder:text-[#a8a29e] focus:border-[#ff4d00]"
                />
                <button
                  type="submit"
                  aria-label="Rechercher"
                  className="absolute right-1 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full"
                >
                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="#ff4d00" strokeWidth="2">
                    <circle cx="9" cy="9" r="6" />
                    <path d="M14 14l4 4" strokeLinecap="round" />
                  </svg>
                </button>
              </form>

              {/* Language switcher */}
              <div className="relative" ref={langMenuRef}>
                <button
                  onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                  className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#83c5be] transition-colors"
                  style={{ color: header.text }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = header.hover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  aria-label={t('nav.language')}
                >
                  <span className="text-base leading-none">{LANGUAGES[language]?.flag}</span>
                  <span className="font-medium uppercase">{language}</span>
                  <svg className={`h-4 w-4 transition-transform duration-200 ${isLangMenuOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isLangMenuOpen && (
                  <div className="absolute right-0 mt-2 w-44 bg-white rounded-lg shadow-lg ring-1 ring-black/5 py-1 z-50">
                    {Object.values(LANGUAGES).map((lng) => (
                      <button
                        key={lng.code}
                        onClick={() => { setLanguage(lng.code); setIsLangMenuOpen(false); }}
                        className={`flex items-center gap-2 w-full px-4 py-2 text-sm text-left hover:bg-gray-50 ${language === lng.code ? 'font-semibold' : 'text-gray-700'}`}
                        style={language === lng.code ? { color: colors.primary } : {}}
                      >
                        <span className="text-base leading-none">{lng.flag}</span>
                        <span>{lng.label}</span>
                        {language === lng.code && (
                          <svg className="ml-auto h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: colors.primary }}>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Cart Icon - always visible (public) */}
              <CartIcon color={header.text} hoverColor={header.accent} hoverBackground={header.hover} />

              {isLoggedIn ? (
                <div className="flex space-x-3 items-center">
                  {/* Existing profile dropdown */}
                  <div className="relative ml-3" ref={userMenuRef}>
                    <button
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      className="flex items-center gap-2 text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#83c5be] rounded-full"
                    >
                      <div className="h-8 w-8 rounded-full overflow-hidden border-2 border-gray-200">
                        <img 
                          src={getUserAvatar()} 
                          alt={`${user?.firstName || 'User'}'s profile`}
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            // Fallback if image fails to load
                            e.target.src = "https://ui-avatars.com/api/?name=User&background=4983a9&color=fff";
                          }}
                        />
                      </div>
                      <span className="text-sm font-medium hidden lg:block" style={{ color: header.text }}>
                        {user?.fullName?.split('@')[0] || 'User'}
                      </span>
                      <svg className={`h-4 w-4 transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} style={{ color: header.muted }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    <AnimatePresence>
                      {isUserMenuOpen && (
                        <motion.div
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          variants={fadeInAnimation}
                          className="absolute right-0 w-60 mt-3 origin-top-right bg-white rounded-lg shadow-xl border border-gray-100 py-1 z-50"
                        >
                          <div className="py-3 px-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white rounded-t-lg">
                            <p className="text-xs text-gray-500 uppercase tracking-wider">Connecté en tant que</p>
                            <p className="text-sm font-medium text-gray-900 truncate mt-0.5">{user?.fullName || 'Utilisateur'}</p>
                          </div>

                          <div className="py-2">
                            <Link to="/profile" className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150">
                              <svg className="h-4 w-4 mr-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              <span>Mon Profil</span>
                            </Link>
                            {/* <a href="#orders" className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150">
                              <svg className="h-4 w-4 mr-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                              </svg>
                              <span>Commandes & Retours</span>
                            </a>
                            <a href="#saved" className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150">
                              <svg className="h-4 w-4 mr-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                              </svg>
                              <span>Articles Sauvegardés</span>
                            </a> */}
                          </div>

                          <div className="border-t border-gray-100 pt-2 pb-1">
                            <button
                              onClick={handleLogout}
                              className="flex w-full items-center px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150"
                            >
                              <svg className="h-4 w-4 mr-3 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                              </svg>
                              <span>Déconnexion</span>
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <Link
                    to={ROUTES.login}
                    className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full border border-[#ffac86] bg-white px-4 py-2 text-sm font-semibold text-[#ff4d00] transition-colors hover:bg-[#ffe3d6] focus:outline-none focus:ring-2 focus:ring-[#ff4d00]"
                  >
                    Se connecter
                  </Link>
                  {/* <Link
                    to={ROUTES.signup}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#4983a9] to-[#1d6b63] border border-transparent rounded-lg hover:from-[#123c39] hover:to-[#195f58] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#83c5be] shadow-md transition-all duration-200 relative overflow-hidden group"
                  >
                    <span className="absolute top-0 left-0 w-full h-full bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></span>
                    <span className="relative">S'inscrire</span>
                  </Link> */}
                </div>
              )}
            </div>

            <div className="flex items-center md:hidden">
              <div className="flex space-x-2">
                {/* Cart Icon - always visible (public) */}
                <CartIcon color={header.text} hoverColor={header.accent} hoverBackground={header.hover} />

                {isLoggedIn && (
                  <button
                    onClick={() => setIsSearchOpen(true)}
                    className="p-2 rounded-full focus:outline-none focus:ring-2 focus:ring-[#83c5be] transition-colors"
                    style={{ color: header.text }}
                    aria-label="Search"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </button>
                )}
              </div>

              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                type="button"
                className="ml-2 inline-flex items-center justify-center p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#83c5be]"
                style={{ color: header.text }}
                aria-expanded="false"
              >
                <span className="sr-only">{isMenuOpen ? 'Close menu' : 'Open menu'}</span>
                {!isMenuOpen ? (
                  <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>
      </header>

      {/* Mobile menu drawer - Enhanced Professional Design */}
      {isMenuOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-40 lg:hidden"
        >
          {/* Backdrop with blur effect */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setIsMenuOpen(false)}
          ></motion.div>

          {/* Slide-in menu panel */}
          <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="relative flex flex-col w-[85%] max-w-xs h-full bg-white shadow-2xl overflow-hidden"
          >
            {/* Top section with logo and close button */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <Link to={ROUTES.home} className="group flex items-center space-x-2" onClick={() => setIsMenuOpen(false)}>
                <div className="relative">
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-[#4983a9] to-[#83c5be] rounded-full blur opacity-60 group-hover:opacity-80 transition duration-300"></div>
                  <div className="relative w-8 h-8 flex items-center justify-center rounded-full bg-white shadow-sm overflow-hidden">
                    {brandingLoaded ? (
                      <img
                        src={storeLogo || "/logo.jpg"}
                        alt="Brand Logo"
                        className="w-full h-full object-cover"
                        width="32"
                        height="32"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 animate-pulse" aria-hidden="true" />
                    )}
                  </div>
                </div>
                {brandingLoaded ? (
                  <span className="text-[#4983a9] font-bold text-lg tracking-tight">{shopName}</span>
                ) : (
                  <span className="h-5 w-28 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
                )}
              </Link>

              <button
                type="button"
                className="rounded-full p-2 flex items-center justify-center text-gray-400 hover:text-[#4983a9] hover:bg-gray-50 transition-all duration-200 focus:outline-none"
                onClick={() => setIsMenuOpen(false)}
              >
                <span className="sr-only">Close menu</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* User profile section - Only shown when logged in */}
            {isLoggedIn && (
              <div className="bg-gradient-to-r from-[#4983a9]/5 to-[#83c5be]/5">
                <div className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#4983a9] to-[#83c5be] flex items-center justify-center text-white shadow-md">
                      <span className="text-lg font-semibold">{user?.fullName?.charAt(0).toUpperCase() || 'U'}</span>
                    </div>
                    <div>
                      <h3 className="text-base font-medium text-gray-900">{user?.fullName || 'User'}</h3>
                      <p className="text-sm text-gray-500 truncate max-w-[200px]">{user?.email || ''}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Main navigation links */}
            <div className="flex-1 overflow-y-auto py-4 px-3">
              <div className="space-y-1">
                {/* Navigation section heading */}
                <div className="px-3 py-2">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Navigation</h3>
                </div>

                {/* Navigation links with enhanced visual treatments */}
                <Link
                  to={ROUTES.home}
                  className={`flex items-center px-3 py-3 rounded-lg transition-all duration-200 ${location.pathname === ROUTES.home
                      ? 'bg-gradient-to-r from-[#4983a9]/10 to-[#83c5be]/10 text-[#4983a9] font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <svg className={`mr-3 h-5 w-5 ${location.pathname === ROUTES.home ? 'text-[#4983a9]' : 'text-gray-500'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  <span className="text-base">{t('nav.home')}</span>
                  {location.pathname === ROUTES.home && (
                    <span className="ml-auto h-2 w-2 rounded-full" style={{ backgroundColor: colors.primary }}></span>
                  )}
                </Link>

                {/* Admin section - Only shown for admin users */}
                {isLoggedIn && (
                  <Link
                    to={adminHome}
                    className={`flex items-center px-3 py-3 rounded-lg transition-all duration-200 ${location.pathname.startsWith(ROUTES.admin)
                        ? 'font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    style={location.pathname.startsWith(ROUTES.admin) ? 
                      { 
                        background: `linear-gradient(to right, ${colors.primary}10, ${colors.secondary}10)`,
                        color: colors.primary 
                      } : {}
                    }
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg 
                      className="mr-3 h-5 w-5" 
                      style={{ color: location.pathname.startsWith(ROUTES.admin) ? colors.primary : '#6b7280' }}
                      xmlns="http://www.w3.org/2000/svg" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-base">{t('nav.administration')}</span>
                    {location.pathname.startsWith(ROUTES.admin) && (
                      <span className="ml-auto h-2 w-2 rounded-full" style={{ backgroundColor: colors.primary }}></span>
                    )}
                  </Link>
                )}

                {canSeeSettings && (
                  <Link
                    to="/admin/settings"
                    className={`flex items-center px-3 py-3 rounded-lg transition-all duration-200 ${location.pathname === '/admin/settings'
                        ? 'font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    style={location.pathname === '/admin/settings' ?
                      {
                        background: `linear-gradient(to right, ${colors.primary}10, ${colors.secondary}10)`,
                        color: colors.primary
                      } : {}
                    }
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg
                      className="mr-3 h-5 w-5"
                      style={{ color: location.pathname === '/admin/settings' ? colors.primary : '#6b7280' }}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-base">{t('nav.parametrage')}</span>
                    {location.pathname === '/admin/settings' && (
                      <span className="ml-auto h-2 w-2 rounded-full" style={{ backgroundColor: colors.primary }}></span>
                    )}
                  </Link>
                )}

                {/* Documentation link */}
                <Link
                  to="/documentation"
                  className={`flex items-center px-3 py-3 rounded-lg transition-all duration-200 ${location.pathname === '/documentation'
                      ? 'bg-gradient-to-r from-[#4983a9]/10 to-[#83c5be]/10 text-[#4983a9] font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <svg className={`mr-3 h-5 w-5 ${location.pathname === '/documentation' ? 'text-[#4983a9]' : 'text-gray-500'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  <span className="text-base">{t('nav.documentation')}</span>
                  {location.pathname === '/documentation' && (
                    <span className="ml-auto h-2 w-2 rounded-full bg-[#4983a9]"></span>
                  )}
                </Link>

                {/* Language switcher (mobile) */}
                <div className="px-3 pt-3 mt-2 border-t border-gray-100">
                  <p className="text-xs font-medium text-gray-400 uppercase mb-2">{t('nav.language')}</p>
                  <div className="flex gap-2">
                    {Object.values(LANGUAGES).map((lng) => (
                      <button
                        key={lng.code}
                        onClick={() => setLanguage(lng.code)}
                        className={`flex items-center gap-2 flex-1 px-3 py-2 rounded-lg text-sm border transition-colors ${language === lng.code ? 'font-semibold' : 'text-gray-700 border-gray-200 hover:bg-gray-50'}`}
                        style={language === lng.code ? { borderColor: colors.primary, color: colors.primary, background: `${colors.primary}10` } : {}}
                      >
                        <span className="text-base leading-none">{lng.flag}</span>
                        <span>{lng.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom actions section */}
            <div className="border-t border-gray-200 px-4 py-4">
              {!isLoggedIn ? (
                <div className="space-y-3">
                  <Link
                    to={ROUTES.login}
                    className="flex justify-center items-center px-4 py-2.5 text-sm font-medium text-[#4983a9] bg-white border border-[#4983a9]/30 rounded-lg hover:bg-gray-50 focus:outline-none shadow-sm transition-all duration-200 w-full"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Se connecter
                  </Link>
                 
                </div>
              ) : (
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMenuOpen(false);
                  }}
                  className="flex items-center w-full px-4 py-2.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors duration-200"
                >
                  <svg className="mr-2 h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Déconnexion
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
};

export default Navbar;

